import {Image} from 'coreClasses/images/Image';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {rebuildPathWithParams} from 'coreUtils/url';
import {generateImagePlaceholder} from 'coreUtils/utils';
import {USE_SVG_PLACEHOLDERS} from 'brandConstants';

/**
 * Represents ContentfulImage
 */
class ContentfulAsset implements Image {
	private readonly src: string;
	private readonly title: string;
	private readonly description: string;
	private readonly width: number;
	private readonly height: number;
	private readonly placeHolderDivisor: number = 15;
	private readonly lossless: boolean;

	/**
	 * @constructor
	 * @todo Implement type for image
	 * @param {any} image - contentful image data
	 */
	constructor(image, lossless: boolean = false) {
		this.src = image.url;
		this.title = image.title;
		this.description = image.description;
		this.width = image.width || 0;
		this.height = image.height || 0;
		this.lossless = lossless;
	}

	/**
	 * Returns the image size recalculated based on the given breakpoint
	 * @param {string} breakPoint the breakpoint key
	 * @return {width: number, height: number} an object containing width and height
	 * @private
	 */
	private getBreakPointSize(breakPoint: string): {width: number; height: number} {
		const systemBreakPoints = ViewportMgr.getBreakpointsMap(true);
		const breakPointsKeys = Object.keys(systemBreakPoints);
		const breakPointIndex = breakPointsKeys.indexOf(breakPoint);
		const nextBreakPointIndex =
			breakPointIndex < breakPointsKeys.length - 1 ? breakPointIndex + 1 : breakPointIndex;
		const width = systemBreakPoints[breakPointsKeys[nextBreakPointIndex]];

		if (width < this.width) {
			const resizeRatio = width / this.width;
			const height = Math.round(this.height * resizeRatio);

			return {width, height};
		} else {
			return {
				width: this.width,
				height: this.height,
			};
		}
	}

	/**
	 * Returns the url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getUrl(breakPoint: string = null, dpi: number = 1): string {
		// Contentful asset max size is 4000 width and height
		const contentfulAssetMaxSize = 4000;

		if (!this.src) {
			return '';
		}

		const size = this.getBreakPointSize(breakPoint);
		const width = Math.round(Math.min(this.width, size.width * dpi));
		const height = Math.round(Math.min(this.height, size.height * dpi));
		const imageParameters = {
			w: width > contentfulAssetMaxSize ? contentfulAssetMaxSize : width,
			h: height > contentfulAssetMaxSize ? contentfulAssetMaxSize : height,
			fm: 'avif',
		};

		if (this.lossless) {
			imageParameters.q = '100';
		}

		return rebuildPathWithParams(this.src, imageParameters) || '';
	}

	/**
	 * Returns the placeholder url recalculated based on the given breakpoint and dpi
	 * @param {string} breakPoint the breakpoint key
	 * @param {number} dpi the desired dpi
	 * @return {string} the calculated image url
	 */
	getPlaceholderUrl(breakPoint: string = null, dpi: number = 1): string {
		const size = this.getBreakPointSize(breakPoint);

		if (USE_SVG_PLACEHOLDERS) {
			return generateImagePlaceholder(this.getWidth(), this.getHeight());
		} else {
			const pathWithParams = rebuildPathWithParams(this.src, {
				w: Math.round(size.width / this.placeHolderDivisor) * dpi,
				h: Math.round(size.height / this.placeHolderDivisor) * dpi,
				fm: 'avif',
			});

			return this.src ? pathWithParams : '';
		}
	}

	/**
	 * Returns the image width as string
	 * @return {Number} the image width
	 */
	getWidth() : number {
		return this.width;
	}

	/**
	 * Returns the image height as string
	 * @return {Number} the image height
	 */
	getHeight() : number {
		return this.height;
	}

	/**
	 * Returns the image title
	 * @return {string} the image title
	 */
	getTitle(): string {
		return this.title;
	}

	/**
	 * Returns the image description
	 * @return {string} the image description
	 */
	getDescription(): string {
		return this.description;
	}

	/**
	 * Returns the image alternative text
	 * @return {string} the image alternative text
	 */
	getAlt(): string {
		return this.description ?? this.title ?? '';
	}
}

export default ContentfulAsset;
